import React, { useState, useEffect, useMemo } from 'react';
import { Table, Tag, Input, Select, Drawer, Button, Modal, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ShieldAlert,
  Search,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Clock,
  Eye,
  Camera,
  AlertCircle,
  CheckCircle,
  Truck,
  Package,
  User,
  Phone,
  Mail,
  Calendar,
  Layers,
  ArrowRight,
  Send,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchAllIssues, respondToIssue } from '../../services/user';



// Human-friendly subcategory dictionary
const SUBCATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  FLM01: { label: 'Order Not Delivered', color: 'red' },
  FLM02: { label: 'Delivery Delay / No Date', color: 'orange' },
  FLM03: { label: 'Delivery Agent Delayed', color: 'orange' },
  FLM04: { label: 'Wrong Delivery Address', color: 'volcano' },
  FLM05: { label: 'Package Opened / Transit Damage', color: 'red' },
  FLM06: { label: 'Partial Delivery', color: 'gold' },
  FLM07: { label: 'Delivery Executive Issue', color: 'warning' },
  FLM08: { label: 'General Delivery Issue', color: 'orange' },
  ITM01: { label: 'Missing Item in Package', color: 'volcano' },
  ITM02: { label: 'Defective / Broken Item', color: 'red' },
  ITM03: { label: 'Wrong Item Received', color: 'purple' },
  ITM04: { label: 'Quality / Damaged Product', color: 'magenta' },
  ITM05: { label: 'Poor Packaging', color: 'gold' },
  ORD01: { label: 'Cancellation Request', color: 'blue' },
  ORD02: { label: 'Billing Discrepancy', color: 'cyan' },
  ORD03: { label: 'Invoice Not Received', color: 'geekblue' },
};

interface ComplainantAction {
  complainant_action: string;
  short_desc: string;
  updated_at: string;
  updated_by?: {
    org?: { name?: string };
    contact?: { phone?: string; email?: string };
    person?: { name?: string };
  };
}

interface RespondentAction {
  respondent_action: string;
  short_desc: string;
  updated_at: string;
  cascaded_level?: number;
  updated_by?: {
    org?: { name?: string };
    contact?: { phone?: string; email?: string };
    person?: { name?: string };
  };
}

interface IssueRecord {
  _id: string;
  orgEmail?: string;
  orgName?: string;
  orgMobile?: string;
  context?: {
    domain?: string;
    bap_id?: string;
    bpp_id?: string;
    transaction_id?: string;
    timestamp?: string;
  };
  message?: {
    issue?: {
      id: string;
      category: string;
      sub_category: string;
      status: string;
      issue_type?: string;
      created_at: string;
      updated_at?: string;
      expected_response_time?: { duration?: string };
      expected_resolution_time?: { duration?: string };
      complainant_info?: {
        person?: { name?: string };
        contact?: { phone?: string; email?: string };
      };
      order_details?: {
        id: string;
        orderDetailsId?: string;
        state?: string;
        provider_id?: string;
        provider_name?: string;
        items?: { id: string; quantity: number }[];
        fulfillments?: { id: string; state: string }[];
      };
      description?: {
        short_desc?: string;
        long_desc?: string;
        images?: string[];
      };
      issue_actions?: {
        complainant_actions?: ComplainantAction[];
        respondent_actions?: RespondentAction[];
      };
      resolution?: {
        action_triggered?: string;
        short_desc?: string;
        long_desc?: string;
      };
    };
  };
}

const DEFAULT_USER_ID = '6a180e5debe41f7e14571243';

// Helper to determine the latest real-time status from inside the timeline
export const getTimelineStatus = (record: IssueRecord | null | undefined): string => {
  if (!record) return 'OPEN';
  const issue = record.message?.issue;
  if (!issue) return 'OPEN';

  const actions: { status: string; time: number }[] = [];

  const comps = issue.issue_actions?.complainant_actions || [];
  comps.forEach((c) => {
    if (c.complainant_action) {
      actions.push({
        status: c.complainant_action.toUpperCase(),
        time: new Date(c.updated_at || 0).getTime(),
      });
    }
  });

  const resps = issue.issue_actions?.respondent_actions || [];
  resps.forEach((r) => {
    if (r.respondent_action) {
      actions.push({
        status: r.respondent_action.toUpperCase(),
        time: new Date(r.updated_at || 0).getTime(),
      });
    }
  });

  // Sort chronologically ascending
  actions.sort((a, b) => a.time - b.time);

  if (actions.length > 0) {
    let latest = actions[actions.length - 1].status;
    if (latest === 'CLOSE') return 'CLOSED';
    if (latest === 'ESCALATE') return 'ESCALATED';
    return latest;
  }

  // Fallback to resolution or top-level status
  if (issue.resolution?.action_triggered) return 'RESOLVED';
  return (issue.status || 'OPEN').toUpperCase();
};

export const renderStatusTag = (status: string) => {
  let tagColor = 'default';
  let icon: React.ReactNode = null;

  if (status === 'OPEN') {
    tagColor = 'red';
    icon = <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse mr-1" />;
  } else if (status === 'PROCESSING') {
    tagColor = 'blue';
    icon = <Clock size={11} className="inline mr-1" />;
  } else if (status === 'CASCADED') {
    tagColor = 'orange';
    icon = <Truck size={11} className="inline mr-1" />;
  } else if (status === 'RESOLVED' || status === 'CLOSED') {
    tagColor = 'green';
    icon = <CheckCircle size={11} className="inline mr-1" />;
  } else if (status === 'ESCALATED') {
    tagColor = 'volcano';
    icon = <AlertCircle size={11} className="inline mr-1" />;
  }

  return (
    <Tag color={tagColor} className="flex items-center w-fit px-2.5 py-0.5 font-semibold text-xs rounded-full m-0 shadow-sm">
      {icon}
      {status}
    </Tag>
  );
};

const IssueList: React.FC = () => {
  const [issues, setIssues] = useState<IssueRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeUserId, setActiveUserId] = useState<string>(() => {
    return localStorage.getItem('ud-user') || DEFAULT_USER_ID;
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected Issue for Drawer
  const [selectedIssue, setSelectedIssue] = useState<IssueRecord | null>(null);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

  // Image Lightbox Preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Copy helper
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const loadIssues = async () => {
    try {
      setLoading(true);
      const res = await fetchAllIssues(activeUserId, pagination.current, pagination.pageSize);
      if (res && res.data) {
        setIssues(res.data);
        setTotalCount(res.count || 0);
      } else {
        setIssues([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('Failed to load IGM issues:', err);
      toast.error('Failed to load grievances');
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIssues();
  }, [activeUserId, pagination.current, pagination.pageSize]);

  // Client-side filtering for fast interactive search
  const filteredIssues = useMemo(() => {
    return issues.filter((record) => {
      const issue = record.message?.issue;
      if (!issue) return false;

      // Status Filter using timeline status
      if (statusFilter !== 'ALL') {
        const issueStatus = getTimelineStatus(record);
        if (issueStatus !== statusFilter) return false;
      }

      // Category Filter
      if (categoryFilter !== 'ALL') {
        const cat = (issue.category || '').toUpperCase();
        if (cat !== categoryFilter) return false;
      }

      // Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const orderId = (issue.order_details?.id || '').toLowerCase();
        const issueId = (issue.id || '').toLowerCase();
        const buyerName = (issue.complainant_info?.person?.name || '').toLowerCase();
        const buyerPhone = (issue.complainant_info?.contact?.phone || '').toLowerCase();
        const shortDesc = (issue.description?.short_desc || '').toLowerCase();

        return (
          orderId.includes(q) ||
          issueId.includes(q) ||
          buyerName.includes(q) ||
          buyerPhone.includes(q) ||
          shortDesc.includes(q)
        );
      }

      return true;
    });
  }, [issues, statusFilter, categoryFilter, searchQuery]);

  // KPI Calculations using real timeline status
  const kpis = useMemo(() => {
    let open = 0;
    let closed = 0;
    let fulfillmentCount = 0;
    let itemCount = 0;

    issues.forEach((item) => {
      const iss = item.message?.issue;
      if (!iss) return;
      const st = getTimelineStatus(item);
      if (st === 'CLOSED' || st === 'RESOLVED') closed++;
      else open++;
      if (iss.category === 'FULFILLMENT') fulfillmentCount++;
      if (iss.category === 'ITEM') itemCount++;
    });

    return {
      total: totalCount || issues.length,
      open,
      closed,
      fulfillmentCount,
      itemCount,
    };
  }, [issues, totalCount]);

  // Check if issue is already resolved
  const isIssueResolved = (issueRecord: IssueRecord | null): boolean => {
    if (!issueRecord) return true;
    const tStatus = getTimelineStatus(issueRecord);
    if (tStatus === 'RESOLVED' || tStatus === 'CLOSED') return true;
    const issue = issueRecord.message?.issue;
    if (!issue) return true;
    const status = (issue.status || '').toUpperCase();
    if (status === 'RESOLVED' || status === 'CLOSED') return true;
    if (issue.resolution && issue.resolution.action_triggered) return true;
    const respondentActions = issue.issue_actions?.respondent_actions || [];
    if (respondentActions.some((a) => (a.respondent_action || '').toUpperCase() === 'RESOLVED')) {
      return true;
    }
    return false;
  };

  // Check if issue is ITEM, ITM04
  const isItemDamage = (issueRecord: IssueRecord | null): boolean => {
    if (!issueRecord) return false;
    const issue = issueRecord.message?.issue;
    return issue?.category === 'ITEM' && issue?.sub_category === 'ITM04';
  };

  // "Take a Action" Form State
  const [showActionModal, setShowActionModal] = useState<boolean>(false);
  const [actionChoice, setActionChoice] = useState<'NO-ACTION' | 'CANCEL' | 'REFUND'>('NO-ACTION');
  const [shortDesc, setShortDesc] = useState<string>('');
  const [longDesc, setLongDesc] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const openDrawer = (record: IssueRecord) => {
    setSelectedIssue(record);
    setDrawerVisible(true);
    setShowActionModal(false);
    setActionChoice('NO-ACTION');
    setShortDesc('');
    setLongDesc('');
    setRefundAmount('');
  };

  const handleSendResponse = async () => {
    if (!selectedIssue) return;
    const txId = selectedIssue.context?.transaction_id || (selectedIssue as any).logisticsTransactionId;
    if (!txId) {
      toast.error('Transaction ID missing from this issue');
      return;
    }
    if (!shortDesc.trim()) {
      toast.error('Please enter short description');
      return;
    }

    if (actionChoice === 'REFUND' && (!refundAmount || Number(refundAmount) <= 0)) {
      toast.error('Please enter a valid refund amount');
      return;
    }

    if (actionChoice !== 'REFUND' && !longDesc.trim()) {
      toast.error('Please enter long description');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        transaction_id: txId,
        respondent_action: 'RESOLVED',
        action_triggered: actionChoice,
        short_desc: shortDesc.trim(),
        long_desc: actionChoice === 'REFUND' ? shortDesc.trim() : longDesc.trim(),
        refund_amount: actionChoice === 'REFUND' ? Number(refundAmount) || 0 : undefined,
      };

      const res = await respondToIssue(payload);
      if (res && (res.success || res.message === 'Sucess')) {
        toast.success('Action submitted successfully to ONDC!');
        setShowActionModal(false);
        setShortDesc('');
        setLongDesc('');
        setRefundAmount('');
        await loadIssues();
        setDrawerVisible(false);
      } else {
        toast.error(res?.message || 'Failed to submit action');
      }
    } catch (err: any) {
      console.error('Error submitting action:', err);
      toast.error(err.response?.data?.message || err.message || 'Error submitting action');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ant Design Table Columns
  const columns: ColumnsType<IssueRecord> = [
    {
      title: 'Issue & Date',
      key: 'issueId',
      width: 220,
      render: (_, record) => {
        const issue = record.message?.issue;
        if (!issue) return '-';
        const isCopied = copiedId === issue.id;
        const dateStr = issue.created_at ? new Date(issue.created_at).toLocaleDateString() : '';
        const timeStr = issue.created_at ? new Date(issue.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

        return (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 font-mono text-xs font-semibold text-gray-800">
              <span className="truncate max-w-[120px]" title={issue.id}>
                {issue.id.slice(0, 8)}...{issue.id.slice(-4)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(issue.id, issue.id);
                }}
                className="text-gray-400 hover:text-green-600 transition p-0.5 rounded"
                title="Copy Full Issue UUID"
              >
                {isCopied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
              </button>
            </div>
            <div className="text-[11px] text-gray-500 flex items-center gap-1">
              <Calendar size={11} />
              <span>{dateStr}</span>
              <span className="text-gray-400">•</span>
              <span>{timeStr}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Order ID',
      key: 'orderId',
      width: 170,
      render: (_, record) => {
        const order = record.message?.issue?.order_details;
        if (!order) return '-';
        return (
          <div className="space-y-1">
            <div className="font-semibold text-gray-900 text-xs flex items-center gap-1">
              <span>{order.id}</span>
            </div>
            {order.state && (
              <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                {order.state}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Buyer / Complainant',
      key: 'complainant',
      width: 190,
      render: (_, record) => {
        const comp = record.message?.issue?.complainant_info;
        const bapId = record.context?.bap_id || '';
        return (
          <div className="space-y-1">
            <div className="font-medium text-xs text-gray-900 flex items-center gap-1">
              <User size={12} className="text-gray-400" />
              <span className="truncate max-w-[140px]">{comp?.person?.name || 'Customer'}</span>
            </div>
            {comp?.contact?.phone && (
              <div className="text-[11px] text-gray-500 flex items-center gap-1">
                <Phone size={11} className="text-gray-400" />
                <a href={`tel:${comp.contact.phone}`} className="hover:text-green-600">
                  {comp.contact.phone}
                </a>
              </div>
            )}
            {bapId && (
              <div className="text-[10px] text-blue-600 font-mono truncate max-w-[150px]" title={bapId}>
                BAP: {bapId.split('.')[0] || bapId}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Category & Reason',
      key: 'category',
      width: 220,
      render: (_, record) => {
        const issue = record.message?.issue;
        if (!issue) return '-';
        const subMeta = SUBCATEGORY_LABELS[issue.sub_category] || {
          label: issue.sub_category || 'Grievance',
          color: 'default',
        };

        const hasImages = (issue.description?.images || []).length > 0;

        return (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Tag color={issue.category === 'FULFILLMENT' ? 'blue' : 'purple'} className="font-semibold text-[11px] m-0">
                {issue.category}
              </Tag>
              <Tag color={subMeta.color} className="text-[11px] m-0">
                {issue.sub_category}
              </Tag>
            </div>
            <div className="text-xs text-gray-800 font-medium line-clamp-1" title={subMeta.label}>
              {subMeta.label}
            </div>
            {hasImages && (
              <div className="flex items-center gap-1 text-[11px] text-rose-600 font-medium">
                <Camera size={12} />
                <span>Photo Evidence</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Complaint Summary',
      key: 'summary',
      render: (_, record) => {
        const desc = record.message?.issue?.description;
        return (
          <div className="space-y-0.5">
            <div className="text-xs font-semibold text-gray-900">{desc?.short_desc || 'No summary'}</div>
            <div className="text-xs text-gray-500 line-clamp-2">{desc?.long_desc || ''}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, record) => {
        const status = getTimelineStatus(record);
        return renderStatusTag(status);
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => openDrawer(record)}
          className="bg-green-600 hover:bg-green-700 text-xs flex items-center gap-1 font-medium shadow-sm"
        >
          <Eye size={13} />
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Page Title & Active Provider Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-rose-100 text-rose-700 rounded-lg">
              <ShieldAlert size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Issue & Grievance Management (IGM)</h1>
              <p className="text-xs text-gray-500">
                Live ONDC Network disputes, buyer grievances, and resolution SLA tracking
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
            onClick={loadIssues}
            disabled={loading}
            className="flex items-center gap-1 text-xs"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Grievances</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.total}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Across all order fulfillments</p>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <Layers size={22} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-rose-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-rose-600 uppercase tracking-wider">Action Needed (Open)</p>
            <p className="text-2xl font-bold text-rose-600 mt-1">{kpis.open}</p>
            <p className="text-[11px] text-rose-400 mt-0.5">Within 24h ONDC SLA</p>
          </div>
          <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
            <AlertCircle size={22} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-emerald-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Resolved / Closed</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{kpis.closed}</p>
            <p className="text-[11px] text-emerald-500 mt-0.5">Disputes closed successfully</p>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <CheckCircle size={22} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-purple-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-purple-700 uppercase tracking-wider">Item vs Fulfillment</p>
            <p className="text-2xl font-bold text-purple-700 mt-1">
              {kpis.itemCount} <span className="text-xs font-normal text-gray-400">Items /</span> {kpis.fulfillmentCount}{' '}
              <span className="text-xs font-normal text-gray-400">Logistics</span>
            </p>
            <p className="text-[11px] text-purple-500 mt-0.5">Category breakdown</p>
          </div>
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
            <Package size={22} />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-3">
        {/* Search */}
        <div className="w-full md:w-96">
          <Input
            prefix={<Search size={15} className="text-gray-400" />}
            placeholder="Search by Order ID, Issue ID, Buyer, Phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            className="rounded-lg py-1.5 text-xs"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Status:</span>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-36 text-xs"
              options={[
                { value: 'ALL', label: 'All Status' },
                { value: 'OPEN', label: 'Open' },
                { value: 'PROCESSING', label: 'Processing' },
                { value: 'CASCADED', label: 'Cascaded' },
                { value: 'RESOLVED', label: 'Resolved' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'ESCALATED', label: 'Escalated' },
              ]}
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Category:</span>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              className="w-40 text-xs"
              options={[
                { value: 'ALL', label: 'All Categories' },
                { value: 'ITEM', label: 'Item / Product' },
                { value: 'FULFILLMENT', label: 'Fulfillment / Courier' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Issues Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table
          columns={columns}
          dataSource={filteredIssues}
          rowKey={(record) => record._id || record.message?.issue?.id || Math.random().toString()}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          className="text-xs"
          scroll={{ x: 1000 }}
        />
      </div>

      {/* Investigation & Details Slide-Over Drawer */}
      <Drawer
        title={
          <div className="flex items-center justify-between pr-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="text-rose-600" size={20} />
              <div>
                <h3 className="font-bold text-base text-gray-900 m-0">Issue Investigation & Audit Trail</h3>
                <p className="text-[11px] text-gray-500 m-0">ONDC IGM Complaint Details</p>
              </div>
            </div>
            {selectedIssue?.message?.issue && renderStatusTag(getTimelineStatus(selectedIssue))}
          </div>
        }
        placement="right"
        width={680}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedIssue && (
          <div className="space-y-6">
            {/* Top Status & Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200">
                <span className="text-[11px] text-gray-500 uppercase font-medium block">Issue Status</span>
                <div className="mt-1 flex items-center gap-1.5">
                  {renderStatusTag(getTimelineStatus(selectedIssue))}
                </div>
                <span className="text-[10px] text-gray-400 mt-1 block">
                  {getTimelineStatus(selectedIssue) === 'OPEN'
                    ? 'Awaiting Seller Action'
                    : getTimelineStatus(selectedIssue) === 'PROCESSING'
                    ? 'Under Investigation'
                    : getTimelineStatus(selectedIssue) === 'RESOLVED' || getTimelineStatus(selectedIssue) === 'CLOSED'
                    ? 'Resolution Provided'
                    : 'In Progress'}
                </span>
              </div>

              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200">
                <span className="text-[11px] text-gray-500 uppercase font-medium block">Issue UUID</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono text-xs font-semibold text-gray-900 truncate max-w-[130px]" title={selectedIssue.message?.issue?.id}>
                    {selectedIssue.message?.issue?.id}
                  </span>
                  <button
                    onClick={() => handleCopy(selectedIssue.message?.issue?.id || '', 'drawer-id')}
                    className="text-gray-400 hover:text-green-600 p-1"
                  >
                    <Copy size={13} />
                  </button>
                </div>
                <span className="text-[10px] text-gray-400 mt-1 block">ONDC Ticket ID</span>
              </div>

              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200">
                <span className="text-[11px] text-gray-500 uppercase font-medium block">Order Reference</span>
                <div className="mt-1 flex items-center justify-between">
                  <span className="font-semibold text-xs text-gray-900 truncate max-w-[100px]" title={selectedIssue.message?.issue?.order_details?.id}>
                    {selectedIssue.message?.issue?.order_details?.id}
                  </span>
                  <Tag color="cyan" className="text-[10px] m-0">
                    {selectedIssue.message?.issue?.order_details?.state || 'Accepted'}
                  </Tag>
                </div>
                <span className="text-[10px] text-gray-400 mt-1 block">
                  {selectedIssue.message?.issue?.order_details?.fulfillments?.[0]?.state || 'Order State'}
                </span>
              </div>
            </div>

            {/* Buyer Statement & Attached Evidence */}
            <div className="bg-white p-4 rounded-xl border border-rose-100 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-rose-700 font-bold text-sm border-b pb-2">
                <AlertCircle size={16} />
                <span>Buyer Grievance Statement</span>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-800">
                  {selectedIssue.message?.issue?.description?.short_desc || 'No summary'}
                </p>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {selectedIssue.message?.issue?.description?.long_desc || 'No detailed description provided.'}
                </p>
              </div>

              {/* Photo Evidence Gallery */}
              {(selectedIssue.message?.issue?.description?.images || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                    <Camera size={14} className="text-rose-600" />
                    <span>Uploaded Evidence ({selectedIssue.message?.issue?.description?.images?.length})</span>
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {selectedIssue.message?.issue?.description?.images?.map((imgUrl, idx) => (
                      <div
                        key={idx}
                        onClick={() => setPreviewImage(imgUrl)}
                        className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-green-600 hover:shadow-md transition group"
                      >
                        <img src={imgUrl} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                          <Eye size={18} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Complainant & Respondent Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Complainant Box */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                  <User size={13} /> Complainant (Buyer)
                </span>
                <div className="text-xs text-gray-800 space-y-1">
                  <p className="font-semibold text-gray-900">
                    {selectedIssue.message?.issue?.complainant_info?.person?.name || 'Customer'}
                  </p>
                  {selectedIssue.message?.issue?.complainant_info?.contact?.phone && (
                    <p className="flex items-center gap-1 text-gray-600">
                      <Phone size={12} />
                      <a
                        href={`tel:${selectedIssue.message.issue.complainant_info.contact.phone}`}
                        className="hover:text-green-600"
                      >
                        {selectedIssue.message.issue.complainant_info.contact.phone}
                      </a>
                    </p>
                  )}
                  {selectedIssue.message?.issue?.complainant_info?.contact?.email && (
                    <p className="flex items-center gap-1 text-gray-600">
                      <Mail size={12} />
                      <span className="truncate">{selectedIssue.message.issue.complainant_info.contact.email}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Respondent Box */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                  <ShieldAlert size={13} /> Assigned Seller (Respondent)
                </span>
                <div className="text-xs text-gray-800 space-y-1">
                  <p className="font-semibold text-gray-900">
                    {selectedIssue.orgName || selectedIssue.message?.issue?.order_details?.provider_name || 'Seller'}
                  </p>
                  {selectedIssue.orgMobile && (
                    <p className="flex items-center gap-1 text-gray-600">
                      <Phone size={12} />
                      <span>{selectedIssue.orgMobile}</span>
                    </p>
                  )}
                  {selectedIssue.orgEmail && (
                    <p className="flex items-center gap-1 text-gray-600">
                      <Mail size={12} />
                      <span className="truncate">{selectedIssue.orgEmail}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Audit Trail & Action Timeline */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <span className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock size={15} className="text-green-600" />
                  <span>ONDC Action Timeline & Audit Trail</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-500 font-medium">Timeline Status:</span>
                  {renderStatusTag(getTimelineStatus(selectedIssue))}
                </div>
              </div>

              <div className="relative pl-6 space-y-5 border-l-2 border-gray-200 my-2">
                {/* Combined Chronological Audit Trail */}
                {(() => {
                  const comps = (selectedIssue.message?.issue?.issue_actions?.complainant_actions || []).map((a) => ({
                    ...a,
                    actionType: 'complainant' as const,
                    name: a.complainant_action,
                    time: new Date(a.updated_at || 0).getTime(),
                  }));
                  const resps = (selectedIssue.message?.issue?.issue_actions?.respondent_actions || []).map((a) => ({
                    ...a,
                    actionType: 'respondent' as const,
                    name: a.respondent_action,
                    time: new Date(a.updated_at || 0).getTime(),
                  }));
                  const allActions = [...comps, ...resps].sort((a, b) => a.time - b.time);

                  if (allActions.length === 0) {
                    return <p className="text-xs text-gray-400">No timeline actions recorded yet.</p>;
                  }

                  return allActions.map((action, idx) => {
                    const isComp = action.actionType === 'complainant';
                    const isResolved = action.name === 'RESOLVED';
                    const isProcessing = action.name === 'PROCESSING';
                    const isCascaded = action.name === 'CASCADED';
                    const isEscalate = action.name === 'ESCALATE';

                    const dotBg = isResolved
                      ? 'bg-emerald-500 ring-emerald-200'
                      : isProcessing
                      ? 'bg-blue-500 ring-blue-200'
                      : isCascaded
                      ? 'bg-orange-500 ring-orange-200'
                      : isEscalate
                      ? 'bg-rose-500 ring-rose-200'
                      : 'bg-purple-500 ring-purple-200';

                    const tagColor = isResolved
                      ? 'green'
                      : isProcessing
                      ? 'blue'
                      : isCascaded
                      ? 'orange'
                      : isEscalate
                      ? 'volcano'
                      : isComp
                      ? 'purple'
                      : 'default';

                    return (
                      <div key={`act-${idx}`} className="relative group">
                        <div
                          className={`absolute -left-[31px] top-0 w-3.5 h-3.5 rounded-full border-2 border-white ring-2 ${dotBg}`}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Tag color={tagColor} className="font-semibold text-[10px] m-0">
                              {action.name}
                            </Tag>
                            <span className="text-[10px] text-gray-500 font-medium">
                              ({isComp ? 'Complainant / Buyer' : 'Respondent / Seller'})
                            </span>
                            <span className="text-[11px] text-gray-400">
                              {action.updated_at ? new Date(action.updated_at).toLocaleString() : ''}
                            </span>
                          </div>
                          <p className="text-xs text-gray-800 font-medium mt-1">{action.short_desc}</p>
                          {action.updated_by?.org?.name && (
                            <p className="text-[11px] text-gray-500 font-mono mt-0.5">By: {action.updated_by.org.name}</p>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Final Resolution Box */}
            {selectedIssue.message?.issue?.resolution && (
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 space-y-2">
                <div className="flex items-center justify-between border-b border-emerald-200 pb-2">
                  <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                    <CheckCircle size={15} /> Final Resolution Status
                  </span>
                  <Tag color="green" className="font-bold text-xs m-0">
                    {selectedIssue.message.issue.resolution.action_triggered || 'RESOLVED'}
                  </Tag>
                </div>
                <p className="text-xs text-emerald-900 font-medium">
                  {selectedIssue.message.issue.resolution.short_desc}
                </p>
                {selectedIssue.message.issue.resolution.long_desc && (
                  <p className="text-xs text-emerald-700 whitespace-pre-line bg-white/70 p-3 rounded-lg border border-emerald-100">
                    {selectedIssue.message.issue.resolution.long_desc}
                  </p>
                )}
              </div>
            )}

            {/* Take a Action Section (Only for ITEM, ITM04 and when NOT resolved) */}
            {isItemDamage(selectedIssue) && !isIssueResolved(selectedIssue) && (
              <div className="pt-2">
                {!showActionModal ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-amber-900">Timeline Status:</span>
                        {renderStatusTag(getTimelineStatus(selectedIssue))}
                        <span className="text-[11px] text-amber-700">• Action Required</span>
                      </div>
                      <p className="text-[11px] text-amber-600 m-0 mt-1">This damaged item grievance is awaiting resolution.</p>
                    </div>
                    <Button
                      type="primary"
                      onClick={() => setShowActionModal(true)}
                      className="bg-green-600 hover:bg-green-700 text-xs font-semibold h-10 px-5 rounded-lg shadow-sm"
                    >
                      Take a Action
                    </Button>
                  </div>
                ) : (
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <h4 className="font-bold text-sm text-gray-900 m-0">Take a Action</h4>
                      <Button
                        size="small"
                        type="text"
                        onClick={() => setShowActionModal(false)}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Cancel
                      </Button>
                    </div>

                    {/* 1. Selection from three: No Action, Cancel, Refund */}
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1">
                        Select Action <span className="text-rose-500">*</span>
                      </label>
                      <Select
                        value={actionChoice}
                        onChange={(val) => setActionChoice(val)}
                        className="w-full text-xs"
                        options={[
                          { value: 'NO-ACTION', label: 'No Action' },
                          { value: 'CANCEL', label: 'Cancel' },
                          { value: 'REFUND', label: 'Refund' },
                        ]}
                      />
                    </div>

                    {/* 2. Short description */}
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1">
                        Short Description <span className="text-rose-500">*</span>
                      </label>
                      <Input
                        placeholder="Enter short description"
                        value={shortDesc}
                        onChange={(e) => setShortDesc(e.target.value)}
                        className="rounded-lg text-xs"
                      />
                    </div>

                    {/* 3. If Refund -> Refund Amount; Otherwise -> Long Description */}
                    {actionChoice === 'REFUND' ? (
                      <div>
                        <label className="text-xs font-semibold text-gray-700 block mb-1">
                          Refund Amount (₹) <span className="text-rose-500">*</span>
                        </label>
                        <Input
                          prefix="₹"
                          type="number"
                          placeholder="Enter refund amount"
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          className="rounded-lg text-xs"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs font-semibold text-gray-700 block mb-1">
                          Long Description <span className="text-rose-500">*</span>
                        </label>
                        <Input.TextArea
                          rows={3}
                          placeholder="Enter long description"
                          value={longDesc}
                          onChange={(e) => setLongDesc(e.target.value)}
                          className="rounded-lg text-xs"
                        />
                      </div>
                    )}

                    {/* 4. Submit button */}
                    <div className="pt-2 flex justify-end gap-2">
                      <Button
                        onClick={() => setShowActionModal(false)}
                        className="text-xs rounded-lg"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleSendResponse}
                        loading={isSubmitting}
                        className="bg-green-600 hover:bg-green-700 text-xs font-semibold h-9 px-5 rounded-lg shadow-sm"
                      >
                        Submit
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Image Lightbox Modal */}
      <Modal
        open={!!previewImage}
        footer={null}
        onCancel={() => setPreviewImage(null)}
        centered
        width={700}
      >
        {previewImage && (
          <div className="p-2">
            <img src={previewImage} alt="Evidence Full Preview" className="w-full max-h-[80vh] object-contain rounded-lg" />
            <div className="mt-3 text-right">
              <a
                href={previewImage}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-green-600 hover:underline inline-flex items-center gap-1"
              >
                <span>Open full image in new tab</span>
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default IssueList;
